import { createApi } from "@reduxjs/toolkit/query/react";
import type {
  ContentEitherPartnerRequest,
  ContentEitherPartnerResponse,
  ContentAddressResponse,
  ContentCatalogResponse,
  ContentClauseChangeResponse,
  ContentClauseStructureResponse,
  ContentReleaseCreateResponse,
  ContentReleaseGetResponse,
  ContentReleaseInspectResponse,
  ContentReleaseListResponse,
  ContentReleaseMutationResponse,
  ContentReleaseRevertResponse,
  ContentQuestionChangeResponse,
  ContentQuestionOptionChangeResponse,
  ContentQuestionStructureResponse,
  ContentRuleChangeResponse,
  ContentTermChangeResponse,
  ContentUnstageResponse,
  ContentDiscardResponse,
  ContentFindReplacePreviewResponse,
  ContentFindReplaceResponse,
  ContentQuestionOptionSetResponse,
  ContentSectionStructureResponse,
  ContentSectionAuthoringResponse,
  ContentSectionAuthoringRequest,
  ContentSectionContentRequest,
  ContentSectionContentResponse,
  ContentLibraryAuthoringRequest,
  ContentLibraryAuthoringResponse,
  ContentStateCopyResponse,
  ContentTermLifecycleResponse,
  ContentTermEverywhereResponse,
  ContentClauseHistoryResponse,
  ContentQuestionHistoryResponse,
  ContentConflictCheckResponse,
} from "../../../api/src/admin/content/route";
import type { UnstageKind } from "../../../api/src/admin/content/unstage";
import { baseQuery } from "./utils";

export type { ContentSampleContractResponse } from "../../../api/src/admin/content/route";
export type {
  ContentConflictCheckResponse,
  ContentClauseHistoryResponse,
  ContentQuestionHistoryResponse,
  ContentAddressResponse,
  ContentCatalogResponse,
  ContentFindReplacePreviewResponse,
  ContentReleaseGetResponse,
  ContentReleaseInspectResponse,
  ContentReleaseListResponse,
} from "../../../api/src/admin/content/route";
export type { UnstageKind } from "../../../api/src/admin/content/unstage";

export const contentApi = createApi({
  reducerPath: "contentApi",
  baseQuery,
  tagTypes: [
    "ContentCatalog",
    "ContentRelease",
    "ContentReleaseList",
    "ContentConflictCheck",
  ],
  endpoints: (builder) => ({
    resolveContentAddress: builder.query<
      ContentAddressResponse,
      { documentTemplateId: string; stateId: string; q: string }
    >({
      query: ({ documentTemplateId, stateId, q }) => ({
        url: `admin/content/address?documentTemplateId=${encodeURIComponent(documentTemplateId)}&stateId=${encodeURIComponent(stateId)}&q=${encodeURIComponent(q)}`,
      }),
    }),
    getContentCatalog: builder.query<ContentCatalogResponse, string>({
      query: (documentTemplateId) => ({
        url: `admin/content/catalog?documentTemplateId=${encodeURIComponent(documentTemplateId)}`,
      }),
      providesTags: ["ContentCatalog"],
    }),
    // Every change to one clause or question across all releases. Fetched
    // fresh each time the panel opens: history is read rarely and must not
    // show a stale timeline after a publish.
    getClauseHistory: builder.query<
      ContentClauseHistoryResponse,
      { documentTemplateId: string; clauseTemplateId: string }
    >({
      query: ({ documentTemplateId, clauseTemplateId }) => ({
        url: `admin/content/clauses/${clauseTemplateId}/history?documentTemplateId=${encodeURIComponent(documentTemplateId)}`,
      }),
      keepUnusedDataFor: 0,
    }),
    getQuestionHistory: builder.query<
      ContentQuestionHistoryResponse,
      { documentTemplateId: string; questionId: string }
    >({
      query: ({ documentTemplateId, questionId }) => ({
        url: `admin/content/questions/${questionId}/history?documentTemplateId=${encodeURIComponent(documentTemplateId)}`,
      }),
      keepUnusedDataFor: 0,
    }),
    getContentReleases: builder.query<ContentReleaseListResponse, string>({
      query: (documentTemplateId) => ({
        url: `admin/content/releases?documentTemplateId=${encodeURIComponent(documentTemplateId)}`,
      }),
      providesTags: ["ContentReleaseList"],
    }),
    getContentRelease: builder.query<ContentReleaseGetResponse, string>({
      query: (releaseId) => ({ url: `admin/content/releases/${releaseId}` }),
      providesTags: (_result, _error, releaseId) => [
        { type: "ContentRelease", id: releaseId },
      ],
    }),
    // The release's latest AI conflict check; the card polls it while one
    // is running.
    getConflictCheck: builder.query<ContentConflictCheckResponse, string>({
      query: (releaseId) => ({
        url: `admin/content/releases/${releaseId}/conflict-checks/latest`,
      }),
      providesTags: (_result, _error, releaseId) => [
        { type: "ContentConflictCheck", id: releaseId },
      ],
    }),
    startConflictCheck: builder.mutation<unknown, string>({
      query: (releaseId) => ({
        url: `admin/content/releases/${releaseId}/conflict-checks`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, releaseId) => [
        { type: "ContentConflictCheck", id: releaseId },
      ],
    }),
    setConflictFindingDismissed: builder.mutation<
      unknown,
      { releaseId: string; findingId: string; dismissed: boolean }
    >({
      query: ({ releaseId, findingId, dismissed }) => ({
        url: `admin/content/releases/${releaseId}/conflict-findings/${findingId}`,
        method: "PUT",
        body: { dismissed },
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentConflictCheck", id: releaseId },
      ],
    }),
    inspectContentRelease: builder.query<ContentReleaseInspectResponse, string>(
      {
        query: (releaseId) => ({
          url: `admin/content/releases/${releaseId}/inspect`,
        }),
        providesTags: (_result, _error, releaseId) => [
          { type: "ContentRelease", id: releaseId },
        ],
      },
    ),
    createContentRelease: builder.mutation<
      ContentReleaseCreateResponse,
      { name: string; description?: string; documentTemplateId: string }
    >({
      query: (body) => ({
        url: "admin/content/releases",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ContentReleaseList", "ContentCatalog"],
    }),
    stageClauseChange: builder.mutation<
      ContentClauseChangeResponse,
      {
        releaseId: string;
        clauseTemplateId: string;
        text: string;
        expectedVersion: number;
      }
    >({
      query: ({ releaseId, clauseTemplateId, ...body }) => ({
        url: `admin/content/releases/${releaseId}/clauses/${clauseTemplateId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    stageTermChange: builder.mutation<
      ContentTermChangeResponse,
      {
        releaseId: string;
        termKey: string;
        stateId: string;
        value: string;
        expectedVersion: number;
      }
    >({
      query: ({ releaseId, ...body }) => ({
        url: `admin/content/releases/${releaseId}/terms`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    stageQuestionChange: builder.mutation<
      ContentQuestionChangeResponse,
      {
        releaseId: string;
        questionId: string;
        field: "title" | "description" | "proTip" | "placeholder";
        value: string | null;
        expectedVersion: number;
      }
    >({
      query: ({ releaseId, questionId, ...body }) => ({
        url: `admin/content/releases/${releaseId}/questions/${questionId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    stageQuestionOptionChange: builder.mutation<
      ContentQuestionOptionChangeResponse,
      {
        releaseId: string;
        questionOptionId: string;
        // Either or both: the label, and the pro tip (null clears it).
        value?: string;
        proTip?: string | null;
        expectedVersion: number;
      }
    >({
      query: ({ releaseId, questionOptionId, ...body }) => ({
        url: `admin/content/releases/${releaseId}/question-options/${questionOptionId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    stageLibraryAuthoring: builder.mutation<
      ContentLibraryAuthoringResponse,
      { releaseId: string; change: ContentLibraryAuthoringRequest }
    >({
      query: ({ releaseId, change }) => ({
        url: `admin/content/releases/${releaseId}/library-authoring`,
        method: "POST",
        body: change,
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    stageSectionAuthoring: builder.mutation<
      ContentSectionAuthoringResponse,
      { releaseId: string; change: ContentSectionAuthoringRequest }
    >({
      query: ({ releaseId, change }) => ({
        url: `admin/content/releases/${releaseId}/section-authoring`,
        method: "POST",
        body: change,
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    getSectionContent: builder.query<
      ContentSectionContentResponse,
      { releaseId: string; sectionId: string }
    >({
      query: ({ releaseId, sectionId }) => ({
        url: `admin/content/releases/${releaseId}/sections/${sectionId}/content`,
      }),
      providesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
      ],
      keepUnusedDataFor: 0,
    }),
    stageSectionContent: builder.mutation<
      ContentSectionAuthoringResponse,
      { releaseId: string; change: ContentSectionContentRequest }
    >({
      query: ({ releaseId, change }) => ({
        url: `admin/content/releases/${releaseId}/section-content`,
        method: "POST",
        body: change,
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    stageClauseStructure: builder.mutation<
      ContentClauseStructureResponse,
      {
        releaseId: string;
        expectedVersion: number;
        replacesRevisionId?: string;
        change:
          | {
              operation: "CREATE";
              parentClauseTemplateId?: string | null;
              replacesRevisionId?: string;
              sectionTemplateId: string;
              afterClauseTemplateId: string | null;
              text: string;
              listStyle:
                | "NONE"
                | "NUMERIC"
                | "ALPHA_UPPER"
                | "ALPHA_LOWER"
                | "ROMAN_UPPER"
                | "ROMAN_LOWER";
              appliesToAllStates: boolean;
              stateIds: string[];
              excludedStateIds?: string[];
              pairedRule?: { stableKey: string; expression: unknown };
            }
          | {
              operation: "REPARENT";
              clauseTemplateId: string;
              parentClauseTemplateId: string | null;
              afterClauseTemplateId: string | null;
            }
          | {
              operation: "SET_PRESENTATION";
              clauseTemplateId: string;
              listStyle:
                | "NONE"
                | "NUMERIC"
                | "ALPHA_UPPER"
                | "ALPHA_LOWER"
                | "ROMAN_UPPER"
                | "ROMAN_LOWER";
            }
          | {
              operation: "CREATE_VARIANT";
              clauseTemplateId: string;
              text: string;
              stateIds: string[];
              inheritConditions?: boolean;
            }
          | {
              operation: "REORDER";
              clauseTemplateId: string;
              afterClauseTemplateId: string | null;
              // Another section of the agreement moves the clause there.
              sectionTemplateId?: string | null;
              moveSubtree?: boolean;
            }
          | {
              operation: "SET_APPLICABILITY";
              clauseTemplateId: string;
              appliesToAllStates: boolean;
              stateIds: string[];
              excludedStateIds?: string[];
            }
          | { operation: "RETIRE"; clauseTemplateId: string };
      }
    >({
      query: ({ releaseId, expectedVersion, change, replacesRevisionId }) => ({
        url: `admin/content/releases/${releaseId}/clause-structures`,
        method: "POST",
        body: {
          ...change,
          expectedVersion,
          ...(replacesRevisionId ? { replacesRevisionId } : {}),
        },
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    // A state version of one question or option copy field, for a set of
    // states: SET gives them their own wording, CLEAR returns them to the
    // shared copy.
    stageStateCopy: builder.mutation<
      ContentStateCopyResponse,
      {
        releaseId: string;
        expectedVersion: number;
        questionId: string;
        questionOptionId: string | null;
        field:
          | "TITLE"
          | "DESCRIPTION"
          | "PRO_TIP"
          | "PLACEHOLDER"
          | "OPTION_LABEL"
          | "OPTION_PRO_TIP"
          | "OPTION_HIDDEN";
        stateIds: string[];
        action: "SET" | "CLEAR";
        value: string | null;
      }
    >({
      query: ({ releaseId, ...body }) => ({
        url: `admin/content/releases/${releaseId}/state-copy`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    stageTermLifecycle: builder.mutation<
      ContentTermLifecycleResponse,
      {
        releaseId: string;
        expectedVersion: number;
        change:
          | {
              operation: "CREATE";
              termKey: string;
              values: { stateId: string; value: string }[];
            }
          | { operation: "RETIRE"; termKey: string; stateIds: string[] };
      }
    >({
      query: ({ releaseId, expectedVersion, change }) => ({
        url: `admin/content/releases/${releaseId}/term-lifecycle`,
        method: "POST",
        body: { ...change, expectedVersion },
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    stageTermValueEverywhere: builder.mutation<
      ContentTermEverywhereResponse,
      {
        releaseId: string;
        expectedVersion: number;
        termKey: string;
        stateIds: string[];
        value: string;
      }
    >({
      query: ({ releaseId, ...body }) => ({
        url: `admin/content/releases/${releaseId}/terms/everywhere`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    stageSectionStructure: builder.mutation<
      ContentSectionStructureResponse,
      {
        releaseId: string;
        expectedVersion: number;
        replacesRevisionId?: string;
        sectionTemplateId: string;
        afterSectionTemplateId: string | null;
      }
    >({
      query: ({ releaseId, ...body }) => ({
        url: `admin/content/releases/${releaseId}/section-structures`,
        method: "POST",
        body: { operation: "REORDER", ...body },
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    stageQuestionStructure: builder.mutation<
      ContentQuestionStructureResponse,
      {
        releaseId: string;
        expectedVersion: number;
        replacesRevisionId?: string;
        change:
          | {
              operation: "CREATE";
              questionnaireSectionId: string;
              questionGroupId: string;
              afterQuestionId: string | null;
              title: string;
              description: string | null;
              proTip: string | null;
              placeholder: string | null;
              questionType:
                | "CHECKBOX"
                | "RADIO"
                | "TEXT_BOX"
                | "TEXT_AREA"
                | "DROPDOWN"
                | "LIST_BOX";
              inputType:
                | "TEXT"
                | "CURRENCY"
                | "NUMBER"
                | "DATE"
                | "PHONE"
                | null;
              inputRestriction:
                | "GREATER_THAN_ZERO"
                | "FUTURE_DATE"
                | "PAST_DATE"
                | null;
              isRequired: boolean;
              allowsCustomClause: boolean;
              allowsOtherOption: boolean;
              generatesDispute: boolean;
              appliesToAllStates: boolean;
              stateIds: string[];
              excludedStateIds?: string[];
              options: { value: string; proTip?: string | null }[];
            }
          | {
              operation: "ADD_OPTIONS";
              questionId: string;
              options: { value: string; proTip?: string | null }[];
            }
          | {
              operation: "REORDER";
              questionId: string;
              afterQuestionId: string | null;
              // Another group of the template moves the question there.
              questionGroupId?: string | null;
            }
          | {
              operation: "SET_APPLICABILITY";
              questionId: string;
              appliesToAllStates: boolean;
              stateIds: string[];
              excludedStateIds?: string[];
            }
          | { operation: "RETIRE"; questionId: string }
          | {
              operation: "REMOVE_OPTIONS";
              questionId: string;
              questionOptionIds: string[];
            }
          | {
              // The complete new order of the question's active options.
              operation: "REORDER_OPTIONS";
              questionId: string;
              questionOptionIds: string[];
            };
      }
    >({
      query: ({ releaseId, expectedVersion, change, replacesRevisionId }) => ({
        url: `admin/content/releases/${releaseId}/question-structures`,
        method: "POST",
        body: {
          ...change,
          expectedVersion,
          ...(replacesRevisionId ? { replacesRevisionId } : {}),
        },
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    // The complete desired option list of one question — existing options by
    // id (relabelled if the value changed), new ones without one. Replaces
    // every option change the release already holds for the question.
    stageQuestionOptionSet: builder.mutation<
      ContentQuestionOptionSetResponse,
      {
        releaseId: string;
        questionId: string;
        expectedVersion: number;
        options: { id?: string; value: string; proTip?: string | null }[];
      }
    >({
      query: ({ releaseId, questionId, ...body }) => ({
        url: `admin/content/releases/${releaseId}/questions/${questionId}/options`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    previewFindReplace: builder.mutation<
      ContentFindReplacePreviewResponse,
      {
        releaseId: string;
        find: string;
        replace: string;
        caseSensitive: boolean;
        wholeWord: boolean;
      }
    >({
      query: ({ releaseId, ...body }) => ({
        url: `admin/content/releases/${releaseId}/find-replace/preview`,
        method: "POST",
        body,
      }),
    }),
    stageFindReplace: builder.mutation<
      ContentFindReplaceResponse,
      {
        releaseId: string;
        expectedVersion: number;
        find: string;
        replace: string;
        caseSensitive: boolean;
        wholeWord: boolean;
        clauseTemplateIds?: string[];
      }
    >({
      query: ({ releaseId, ...body }) => ({
        url: `admin/content/releases/${releaseId}/find-replace`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    stageEitherPartnerRule: builder.mutation<
      ContentEitherPartnerResponse,
      { releaseId: string; change: ContentEitherPartnerRequest }
    >({
      query: ({ releaseId, change }) => ({
        url: `admin/content/releases/${releaseId}/rules/either-partner`,
        method: "POST",
        body: change,
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    stageContentRule: builder.mutation<
      ContentRuleChangeResponse,
      {
        releaseId: string;
        expectedVersion: number;
        change:
          | {
              operation: "UPSERT";
              definitionId?: string;
              stableKey?: string;
              targetType: "QUESTION" | "QUESTION_OPTION" | "CLAUSE" | "SECTION";
              effect:
                | "SHOW"
                | "ALLOW"
                | "INCLUDE"
                | "WAIVES_SPOUSAL_SUPPORT"
                | "HIRES_ATTORNEY"
                | "INCLUDES_EMBRYO_CLAUSE";
              targetId: string | null;
              expression: unknown;
              appliesToAllStates: boolean;
              stateIds: string[];
            }
          | { operation: "RETIRE"; definitionId: string };
      }
    >({
      query: ({ releaseId, expectedVersion, change }) => ({
        url: `admin/content/releases/${releaseId}/rules`,
        method: "POST",
        body: { ...change, expectedVersion },
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    unstageRevision: builder.mutation<
      ContentUnstageResponse,
      {
        releaseId: string;
        kind: UnstageKind;
        revisionId: string;
        expectedVersion: number;
      }
    >({
      query: ({ releaseId, kind, revisionId, expectedVersion }) => ({
        url: `admin/content/releases/${releaseId}/revisions/${kind}/${revisionId}`,
        method: "DELETE",
        body: { expectedVersion },
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    submitContentRelease: builder.mutation<
      ContentReleaseMutationResponse,
      {
        releaseId: string;
        expectedVersion: number;
        acknowledgeWarnings?: boolean;
      }
    >({
      query: ({ releaseId, expectedVersion, acknowledgeWarnings }) => ({
        url: `admin/content/releases/${releaseId}/submit`,
        method: "POST",
        body: {
          expectedVersion,
          ...(acknowledgeWarnings ? { acknowledgeWarnings } : {}),
        },
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    withdrawContentRelease: builder.mutation<
      ContentReleaseMutationResponse,
      string
    >({
      query: (releaseId) => ({
        url: `admin/content/releases/${releaseId}/withdraw`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, releaseId) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    approveContentRelease: builder.mutation<
      ContentReleaseMutationResponse,
      { releaseId: string; acknowledgeWarnings?: boolean }
    >({
      query: ({ releaseId, acknowledgeWarnings }) => ({
        url: `admin/content/releases/${releaseId}/approve${acknowledgeWarnings ? "?acknowledgeWarnings=true" : ""}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    rejectContentRelease: builder.mutation<
      ContentReleaseMutationResponse,
      { releaseId: string; reason: string }
    >({
      query: ({ releaseId, reason }) => ({
        url: `admin/content/releases/${releaseId}/reject`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: (_result, _error, { releaseId }) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    publishContentRelease: builder.mutation<
      ContentReleaseMutationResponse,
      string
    >({
      query: (releaseId) => ({
        url: `admin/content/releases/${releaseId}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, releaseId) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
        "ContentCatalog",
      ],
    }),
    discardContentRelease: builder.mutation<ContentDiscardResponse, string>({
      query: (releaseId) => ({
        url: `admin/content/releases/${releaseId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, releaseId) => [
        { type: "ContentRelease", id: releaseId },
        "ContentReleaseList",
      ],
    }),
    createRevertDraft: builder.mutation<ContentReleaseRevertResponse, string>({
      query: (releaseId) => ({
        url: `admin/content/releases/${releaseId}/revert`,
        method: "POST",
      }),
      invalidatesTags: ["ContentReleaseList"],
    }),
  }),
});

export const {
  useResolveContentAddressQuery,
  useGetContentCatalogQuery,
  useGetContentReleasesQuery,
  useGetClauseHistoryQuery,
  useGetQuestionHistoryQuery,
  useGetContentReleaseQuery,
  useInspectContentReleaseQuery,
  useCreateContentReleaseMutation,
  useStageClauseChangeMutation,
  useStageQuestionChangeMutation,
  useStageQuestionOptionChangeMutation,
  useStageClauseStructureMutation,
  useStageSectionStructureMutation,
  useStageSectionAuthoringMutation,
  useGetSectionContentQuery,
  useStageSectionContentMutation,
  useStageLibraryAuthoringMutation,
  useStageStateCopyMutation,
  useStageTermLifecycleMutation,
  useStageTermValueEverywhereMutation,
  useStageQuestionStructureMutation,
  useStageQuestionOptionSetMutation,
  usePreviewFindReplaceMutation,
  useStageFindReplaceMutation,
  useStageContentRuleMutation,
  useStageEitherPartnerRuleMutation,
  useStageTermChangeMutation,
  useUnstageRevisionMutation,
  useSubmitContentReleaseMutation,
  useApproveContentReleaseMutation,
  useWithdrawContentReleaseMutation,
  useRejectContentReleaseMutation,
  usePublishContentReleaseMutation,
  useCreateRevertDraftMutation,
  useDiscardContentReleaseMutation,
  useGetConflictCheckQuery,
  useStartConflictCheckMutation,
  useSetConflictFindingDismissedMutation,
} = contentApi;
